import React from 'react';
import { PageHeader, ScrollReveal, CTASection } from '../components/UI';

const Process = () => {
  const processSteps = [
    {
      num: '01',
      title: 'Discovery & Audit',
      subtitle: 'Understanding Your Business',
      duration: 'Week 1',
      description: 'We start with a deep dive into your business, goals, and current digital presence. This isn\'t a surface-level review—it\'s a comprehensive audit that uncovers exactly what\'s working, what\'s not, and where the opportunities are.',
      activities: [
        'Complete website technical audit (200+ ranking factors)',
        'Competitor analysis and market research',
        'Keyword opportunity assessment',
        'Google Business Profile review',
        'Current ad campaign analysis (if applicable)',
        'Call tracking and analytics setup review'
      ],
      deliverables: [
        'Comprehensive audit report',
        'Competitor benchmarking analysis',
        'Keyword opportunity matrix',
        'Priority recommendations list'
      ]
    },
    {
      num: '02',
      title: 'Strategy Development',
      subtitle: 'Your Custom Roadmap',
      duration: 'Week 2',
      description: 'Based on our audit findings, we develop a custom strategy tailored specifically to your business, market, and goals. No cookie-cutter templates—every strategy is built from the ground up.',
      activities: [
        'Define target keywords and search intent',
        'Map out content and optimization priorities',
        'Design ad campaign structure (if applicable)',
        'Set realistic timeline and milestones',
        'Establish KPIs and success metrics',
        'Create 90-day action plan'
      ],
      deliverables: [
        '90-day strategic roadmap',
        'Keyword targeting strategy',
        'Campaign structure document',
        'KPI tracking dashboard setup'
      ]
    },
    {
      num: '03',
      title: 'Implementation',
      subtitle: 'Executing With Precision',
      duration: 'Weeks 3-8',
      description: 'This is where the magic happens. Our team gets to work implementing your strategy with meticulous attention to detail. You\'ll have a dedicated account manager keeping you informed every step of the way.',
      activities: [
        'Technical SEO fixes and optimizations',
        'On-page content optimization',
        'Google Business Profile optimization',
        'Citation building and cleanup',
        'Google Ads campaign setup and launch',
        'Conversion tracking implementation'
      ],
      deliverables: [
        'Weekly progress updates',
        'Implementation checklist tracking',
        'Campaign performance reports',
        'Bi-weekly strategy calls'
      ]
    },
    {
      num: '04',
      title: 'Optimization & Scaling',
      subtitle: 'Continuous Improvement',
      duration: 'Ongoing',
      description: 'Marketing is never "set it and forget it." We continuously monitor performance, test new approaches, and optimize based on real data. As results improve, we scale what works to accelerate your growth.',
      activities: [
        'Performance monitoring and analysis',
        'A/B testing and optimization',
        'Keyword expansion and refinement',
        'Bid optimization for Google Ads',
        'Content updates and freshness',
        'Competitive response adjustments'
      ],
      deliverables: [
        'Monthly performance reports',
        'ROI and lead tracking',
        'Strategy optimization recommendations',
        'Monthly strategy call'
      ]
    }
  ];

  const whatToExpect = [
    { icon: '📞', title: 'Regular Communication', desc: 'Weekly updates, bi-weekly calls during implementation, monthly strategy sessions ongoing.' },
    { icon: '📊', title: 'Transparent Reporting', desc: 'Real-time dashboards and detailed monthly reports—no vanity metrics, just numbers that matter.' },
    { icon: '🎯', title: 'Dedicated Support', desc: 'Your account manager is always a call or email away. We respond in hours, not days.' },
    { icon: '🔄', title: 'Flexibility', desc: 'Strategies evolve. We adapt based on what\'s working and what\'s changing in your market.' },
  ];

  return (
    <div>
      <PageHeader 
        title="Our Process"
        subtitle="A proven 4-step methodology that's generated millions in revenue for our clients. Here's exactly how we'll work together."
        breadcrumb="Our Process"
      />

      {/* Process Overview */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>The Crescit Method</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                How We Get You Results
              </h2>
              <p style={{ color: '#666', maxWidth: 700, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Every engagement follows our proven 4-step process. It's systematic, transparent, and designed to deliver measurable results as quickly as possible.
              </p>
            </div>
          </ScrollReveal>

          {/* Timeline Steps */}
          <div style={{ position: 'relative' }}>
            {processSteps.map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.15}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '80px 1fr', 
                  gap: 40, 
                  marginBottom: i < processSteps.length - 1 ? 60 : 0,
                  position: 'relative'
                }}>
                  {/* Step Number */}
                  <div style={{ position: 'relative' }}>
                    <div style={{
                      width: 80, height: 80, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ff6b35, #f54e00)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, fontWeight: 800, color: 'white',
                      boxShadow: '0 10px 30px rgba(255,107,53,0.3)'
                    }}>
                      {step.num}
                    </div>
                    {i < processSteps.length - 1 && (
                      <div style={{
                        position: 'absolute', left: '50%', top: 90, width: 2,
                        height: 'calc(100% + 60px)', background: '#eee',
                        transform: 'translateX(-50%)'
                      }} />
                    )}
                  </div>

                  {/* Step Content */}
                  <div style={{ 
                    background: '#f8f9fa', 
                    borderRadius: 20, 
                    padding: 36,
                    border: '1px solid #eee'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <h3 style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>{step.title}</h3>
                        <p style={{ color: '#ff6b35', fontSize: 15, fontWeight: 600 }}>{step.subtitle}</p>
                      </div>
                      <span style={{ 
                        background: 'rgba(255,107,53,0.1)', color: '#ff6b35',
                        padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600
                      }}>
                        {step.duration}
                      </span>
                    </div>

                    <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                      {step.description}
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      <div>
                        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                          What We Do
                        </h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {step.activities.map((activity, j) => (
                            <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
                              <span style={{ color: '#ff6b35', marginTop: 2 }}>•</span> {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                          What You Get
                        </h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {step.deliverables.map((item, j) => (
                            <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, color: '#666', fontSize: 14, lineHeight: 1.5 }}>
                              <span style={{ color: '#22c55e' }}>✓</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Working With Us</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10 }}>
                What to Expect
              </h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {whatToExpect.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ background: 'white', borderRadius: 16, padding: 32, textAlign: 'center', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>{item.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>{item.title}</h3>
                  <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Visualization */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Timeline</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                When You'll See Results
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                We set realistic expectations. Here's a typical timeline for results.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { time: 'Week 1-2', title: 'Foundation', items: ['Audit complete', 'Strategy approved', 'Quick wins identified'] },
              { time: 'Month 1-2', title: 'Implementation', items: ['Technical fixes live', 'Ads running', 'First data coming in'] },
              { time: 'Month 3-4', title: 'Traction', items: ['Rankings improving', 'Lead flow increasing', 'Optimizations applied'] },
              { time: 'Month 6+', title: 'Acceleration', items: ['Dominant rankings', 'Consistent lead flow', 'Scaling what works'] },
            ].map((phase, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ background: '#f8f9fa', borderRadius: 16, padding: 28, height: '100%' }}>
                  <div style={{ color: '#ff6b35', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{phase.time}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 }}>{phase.title}</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {phase.items.map((item, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#666', fontSize: 14 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection 
        title="Ready to Get Started?"
        subtitle="Let's discuss your goals and create a custom strategy for your business."
      />
    </div>
  );
};

export default Process;
